import { BofuTabDocument, DocumentResponse, FORM_STATUS_ENUM, PersonaStructure } from "@interfaces/response";
import { satatusCurrentStep, StepDataSecondGuarantee } from "@interfaces/SecondGuarantee";
import { AddressDataSession } from "@interfaces/store";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface PersonalInfo {
  name: string;
  first_lastname: string;
  second_lastname: string;
  date_birth: string;
  birth_state: string;
  sex: string;
  rfc: string;
  rfc_homoclave: string;
  curp: string;
  code_phone_number: string;
  phone_number: string;
  email: string;
}

interface Expediente {
  information_person_completed: boolean | null;
  identity_validation_completed: boolean | null;
  bank_references_completed: boolean | null;
  personal_information_status?: FORM_STATUS_ENUM | null;
  bank_references_status?: FORM_STATUS_ENUM | null;
}
export interface SecondGuaranteeData {
  id?: string;
  email: string;
  loan_application_id?: string;
  client_details_id?: string;
  user_id?: string;
  personalInfo: PersonalInfo;
  address?: AddressDataSession;
  buroNip: {
    code: string;
    isValid: boolean;
    termsAccepted: boolean;
  };
  check_sat_bureau?: boolean;
  status_config: Array<StepDataSecondGuarantee>;
  curretsStep: satatusCurrentStep;
  digital_file: Expediente;
  documents_loading: boolean;
  documents: Array<BofuTabDocument>;
  doc_anexo_1: DocumentResponse | undefined;
  tax_system_type: string;
  documents_conyuge: Array<BofuTabDocument>;
  doc_anexo_1_conyuge: DocumentResponse | undefined;
}

export interface AppSlice {
  guarantee: SecondGuaranteeData;
  selectedAval?: PersonaStructure;
  conyugeSecondGuarantee: PersonaStructure | undefined;
}

const initialState: AppSlice = {
  guarantee: {
    id: "",
    email: "",
    loan_application_id: "",
    client_details_id: "",
    user_id: "",
    personalInfo: {
      name: "",
      first_lastname: "",
      second_lastname: "",
      date_birth: "",
      birth_state: "",
      code_phone_number: "",
      phone_number: "",
      curp: "",
      rfc: "",
      rfc_homoclave: "",
      sex: "",
      email: "",
    },
    address: {
      id: "",
      street: "",
      no_ext: "",
      no_int: "",
      colony_id: 0,
      state_id: 0,
      city_id: 0,
      locality_id: 0,
      postal_code: "",
      state_name: "",
      locality_name: "",
      city_name: "",
      other_neighborhood: null,
    },
    buroNip: {
      code: "",
      isValid: false,
      termsAccepted: false,
    },
    check_sat_bureau: false,
    status_config: [],
    curretsStep: {
      code: "",
      url: "",
      sequence: 0,
      status_id: "",
    },
    digital_file: {
      bank_references_completed: null,
      identity_validation_completed: null,
      information_person_completed: null,
    },
    documents_loading: false,
    documents: [],
    doc_anexo_1: undefined,
    tax_system_type: "",
    documents_conyuge: [],
    doc_anexo_1_conyuge: undefined,
  },
  selectedAval: {
    name: "",
    last_name: "",
    last_name_2: "",
    rfc: "",
    email: "",
    curp: "",
    person_type_id: "",
    birth_state: 0,
    birth_date: "",
    sex: "",
    loan_application_id: "",
    civil_status_id: null,
    completed: null,
    occupation: null,
    code_phone_number: null,
    phone_number: null,
    own_account: false,
    own_resources: false,
    pep_shareholder: false,
    pep_members: false,
    created_at: "",
    updated_at: "",
    identity_validation_completed: null,
    information_person_completed: null,
    bank_references_completed: null,
    matrimonial_regimes_id: "",
    id: "",
    is_shareholder: false,
    personal_information_status: null,
    bank_references_status: null,
    person_types_detail: [],
    spouse_id: null,
  },
  conyugeSecondGuarantee: undefined,
};

export const secondGuaranteePlaceSlice = createSlice({
  name: "secondGuarante",
  initialState,
  reducers: {
    setUserSecondGuaranteeUser: (state, action: PayloadAction<{ email: string }>) => {
      state.guarantee.email = action.payload.email;
    },
    setUserSecondGuaranteeInfo: (
      state,
      action: PayloadAction<{ id: string; loan_application_id: string; client_details_id: string; user_id: string }>,
    ) => {
      state.guarantee.id = action.payload.id;
      state.guarantee.loan_application_id = action.payload.loan_application_id;
      state.guarantee.client_details_id = action.payload.client_details_id;
      state.guarantee.user_id = action.payload.user_id;
    },

    setUserSecondGuaranteePersonalInfo: (state, action: PayloadAction<PersonalInfo>) => {
      state.guarantee.personalInfo = action.payload;
    },

    setUserSecondGuaranteeRfcCurp: (
      state,
      action: PayloadAction<{ rfc: string; curp: string; rfc_homoclave: string }>,
    ) => {
      state.guarantee.personalInfo.curp = action.payload.curp;
      state.guarantee.personalInfo.rfc_homoclave = action.payload.rfc_homoclave;
      state.guarantee.personalInfo.rfc = action.payload.rfc;
    },
    setUserSecondGuaranteeAddressInfo: (state, action: PayloadAction<AddressDataSession>) => {
      state.guarantee.address = action.payload;
    },
    setSecondGuaranteeBuroNip: (
      state,
      action: PayloadAction<{ code: string; isValid: boolean; termsAccepted: boolean }>,
    ) => {
      state.guarantee.buroNip.code = action.payload.code;
      state.guarantee.buroNip.isValid = action.payload.isValid;
      state.guarantee.buroNip.termsAccepted = action.payload.termsAccepted;
    },

    setSecondGuaranteeCheckSatBureau: (state, action: PayloadAction<{ check_sat_bureau: boolean }>) => {
      state.guarantee.check_sat_bureau = action.payload.check_sat_bureau;
    },
    setStatusConfigSecondGuarantee: (state, action: PayloadAction<Array<StepDataSecondGuarantee>>) => {
      state.guarantee.status_config = action.payload;
    },
    setCurretStepSecondGuarantee: (state, action: PayloadAction<satatusCurrentStep>) => {
      state.guarantee.curretsStep = action.payload;
    },
    setStatusDigitalFileSecondGuarantee: (state, action: PayloadAction<Expediente>) => {
      state.guarantee.digital_file = action.payload;
    },
    setDocumentsLoading: (state, action: PayloadAction<boolean>) => {
      state.guarantee.documents_loading = action.payload;
    },
    setDocumentsSecondGuarantee: (state, action: PayloadAction<Array<BofuTabDocument>>) => {
      state.guarantee.documents = action.payload;
    },
    setDocumentValueSecondGuarantee: (
      state,
      action: PayloadAction<{ document: BofuTabDocument; newValue: DocumentResponse | undefined }>,
    ) => {
      state.guarantee.documents.forEach((doc) => {
        if (doc.file_category_code === action.payload.document.file_category_code) {
          doc.file_value = action.payload.newValue;
        }
      });
    },
    setDocumentsAnexo1SecondGuarantee: (state, action: PayloadAction<DocumentResponse | undefined>) => {
      state.guarantee.doc_anexo_1 = action.payload;
    },
    setDocumentsConyugeSecondGuarantee: (state, action: PayloadAction<Array<BofuTabDocument>>) => {
      state.guarantee.documents_conyuge = action.payload;
    },
    setDocumentValueConyugeSecondGuarantee: (
      state,
      action: PayloadAction<{ document: BofuTabDocument; newValue: DocumentResponse | undefined }>,
    ) => {
      state.guarantee.documents_conyuge.forEach((doc) => {
        if (doc.file_category_code === action.payload.document.file_category_code) {
          doc.file_value = action.payload.newValue;
        }
      });
    },
    setDocumentsAnexo1ConyugeSecondGuarantee: (state, action: PayloadAction<DocumentResponse | undefined>) => {
      state.guarantee.doc_anexo_1_conyuge = action.payload;
    },

    setTaxSystemSecondGuarantee: (state, action: PayloadAction<{ tax_system_type: string }>) => {
      state.guarantee.tax_system_type = action.payload.tax_system_type;
    },

    setSelectedAval: (state, action: PayloadAction<PersonaStructure>) => {
      state.selectedAval = action.payload;
    },
    setConyugeSecondGuarantee: (state, action: PayloadAction<PersonaStructure | undefined>) => {
      state.conyugeSecondGuarantee = action.payload;
    },
  },
});

export const {
  setUserSecondGuaranteeUser,
  setUserSecondGuaranteeInfo,
  setUserSecondGuaranteePersonalInfo,
  setUserSecondGuaranteeRfcCurp,
  setUserSecondGuaranteeAddressInfo,
  setSecondGuaranteeBuroNip,
  setSecondGuaranteeCheckSatBureau,
  setStatusConfigSecondGuarantee,
  setCurretStepSecondGuarantee,
  setStatusDigitalFileSecondGuarantee,
  setDocumentsLoading,
  setDocumentsSecondGuarantee,
  setDocumentValueSecondGuarantee,
  setDocumentsAnexo1SecondGuarantee,
  setTaxSystemSecondGuarantee,
  setSelectedAval,
  setConyugeSecondGuarantee,
  setDocumentsConyugeSecondGuarantee,
  setDocumentValueConyugeSecondGuarantee,
  setDocumentsAnexo1ConyugeSecondGuarantee,
} = secondGuaranteePlaceSlice.actions;

export default secondGuaranteePlaceSlice.reducer;
